import { useMemo, useState } from 'react';
import { AudioTrackTable, AudioTrackTableMessage, type AudioTrackRow } from '../common/AudioTrackTable';
import { useAudioCatalog } from '../../features/audioCatalog/hooks';
import type { AudioCatalogItem } from '../../features/audioCatalog/api';
import { filterAudioCatalogByRole, type AudioCatalogRole } from '../../features/audioCatalog/catalogRoles';
import { brandConfig } from '../../config/brands';
import styles from './AudioCatalogTabPane.module.css';

/** 用途ごとの文言。 */
const ROLE_TEXT: Record<AudioCatalogRole, { noun: string; empty: string }> = {
  bgm: { noun: '楽曲', empty: 'フリー楽曲はまだありません' },
  sound_effect: { noun: '効果音', empty: '提供の効果音はまだありません' },
};

interface AudioCatalogTabPaneProps {
  role: AudioCatalogRole;
  /** 行のクリック。処理中はその行を「取り込み中…」にし、他の行も選べなくする。 */
  onSelect: (item: AudioCatalogItem) => Promise<unknown> | unknown;
}

/**
 * 素材ピッカー (MaterialsPickerModal) の「提供ライブラリ」タブの中身。
 * 運営 / パートナー提供の効果音・フリー楽曲 (audio_catalog_items) を、素材一覧と同じ
 * 一覧テーブル (AudioTrackTable) で出す。出どころは「ライブラリ」列 (パートナー独自 / 共通)。
 * 名前だけを出し、説明文・提供元は出さない (管理用。2026-09-14 / 2026-09-24 ユーザー決定)。
 */
export function AudioCatalogTabPane({ role, onSelect }: AudioCatalogTabPaneProps) {
  const { data: allItems = [], isLoading, isError } = useAudioCatalog(true);
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const text = ROLE_TEXT[role];

  const items = useMemo(() => filterAudioCatalogByRole(allItems, role), [allItems, role]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? items.filter((item) => item.name.toLowerCase().includes(q)) : items;
  }, [items, query]);

  const handleSelect = async (item: AudioCatalogItem) => {
    setBusyId(item.id);
    try {
      await onSelect(item);
    } finally {
      setBusyId(null);
    }
  };

  const rows: AudioTrackRow[] = shown.map((item) => {
    const playable = !!item.preview_url;
    const hasDuration = (item.duration_seconds ?? 0) > 0;
    return {
      id: item.id,
      name: item.name,
      library: item.scope === 'partner' ? `${brandConfig.serviceName}オリジナル` : '共通ライブラリ',
      durationSeconds: item.duration_seconds,
      previewUrl: item.preview_url,
      status: busyId === item.id ? '取り込み中…' : null,
      disabled: busyId != null || !playable || !hasDuration,
      disabledReason: !playable
        ? `現在この${text.noun}は利用できません`
        : !hasDuration
          ? '長さの情報が無いため使用できません'
          : undefined,
      onSelect: () => void handleSelect(item),
    };
  });

  return (
    <div className={styles.pane}>
      {!isLoading && !isError && items.length > 0 && (
        <input
          type="search"
          className={styles.search}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`${text.noun}名で絞り込み`}
          aria-label={`${text.noun}名で絞り込み`}
        />
      )}
      <div className={styles.scroll}>
        {isLoading && <AudioTrackTableMessage>読み込み中…</AudioTrackTableMessage>}
        {isError && <AudioTrackTableMessage>一覧の取得に失敗しました</AudioTrackTableMessage>}
        {!isLoading && !isError && items.length === 0 && <AudioTrackTableMessage>{text.empty}</AudioTrackTableMessage>}
        {!isLoading && !isError && items.length > 0 && shown.length === 0 && (
          <AudioTrackTableMessage>「{query.trim()}」に一致する{text.noun}はありません</AudioTrackTableMessage>
        )}
        {!isLoading && !isError && shown.length > 0 && (
          <AudioTrackTable rows={rows} rowTestId="audio-catalog-row" />
        )}
      </div>
    </div>
  );
}
